import { useMutation, useQuery } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { get, post } from "api";
import { QRScanner } from "organisms/qr-scanner/QRScanner";
import { queryClient } from "pages/Root";

const redeemOrder = debounce(
  ({ scannedCode, orderId, platformId }) => {
    return post("/retailproducts/redeem-order", {
      scannedCode: scannedCode,
      orderId: orderId,
      platformId: platformId,
    });
  },
  500,
  { leading: true, trailing: false }
);

const ScanRedeemRetailProductsOrder = () => {
  const [scannedQRCode, setScannedQRCode] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong - please contact support if this issue persists"
  );

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: redeemOrder,
    onSuccess: () => {
      setScannedQRCode("");
      queryClient.invalidateQueries(["/retailproducts"]);
      navigate(-1);
    },
    onError: (err: any) => {
      if (err.response?.status == 400) {
        setErrorMessage("Unrecognized QR code - please try again");
      }
    },
  });

  const handleShowErrorMessage = useCallback(() => {
    if (mutation.isError) {
      setShowErrorMessage(true);
      return setTimeout(() => {
        setShowErrorMessage(false);
        setScannedQRCode("");
      }, 2000);
    }
  }, [mutation.isError]);

  useEffect(() => {
    const timeoutId = handleShowErrorMessage();
    return () => clearTimeout(timeoutId);
  }, [handleShowErrorMessage]);

  const handleScanResult = useCallback(() => {
    if (
      scannedQRCode &&
      !showErrorMessage &&
      !mutation.isLoading &&
      !mutation.isSuccess
    ) {
      mutation.mutate({
        scannedCode: scannedQRCode,
        orderId: state.orderId,
        platformId: platform?.id,
      });
    }
  }, [scannedQRCode, showErrorMessage, mutation, state.orderId, platform?.id]);

  useEffect(() => {
    handleScanResult();
  }, [handleScanResult]);

  return (
    <QRScanner
      errorMessage={showErrorMessage ? errorMessage : undefined}
      onClose={() => navigate(-1)}
      onResult={setScannedQRCode}
    />
  );
};

export default ScanRedeemRetailProductsOrder;
